import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getApiClient, handleApiError } from '../api_client/get';
import { toast } from 'react-toastify';
import UserContext from './user';
import CartContext from './cart';

const SavedAddressesContext = createContext();

export const SavedAddressesProvider = ({ children }) => {
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const savedAddressesRef = useRef(savedAddresses);
  const { user } = useContext(UserContext);
  const cart = useContext(CartContext);

  useEffect(() => {
    savedAddressesRef.current = savedAddresses;
  }, [savedAddresses]);

  const fetchSavedAddresses = useCallback(() => {
    return getApiClient().getAllMySavedAddresses()
      .then(({ data }) => {
        if (data.success && Array.isArray(data.user_saved_addresses)) {
          setSavedAddresses([...data.user_saved_addresses].sort((a, b) => a.id - b.id));
        } else {
          setSavedAddresses([]);
          if (data.message) toast.error(data.message);
        }
      })
      .catch((error) => {
        setSavedAddresses([]);
        handleApiError(error, (msg) => {
          if (msg) toast.error(msg);
        }, null, true);
      })
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!user) {
      setSavedAddresses([]);
      setLoaded(false);
      return;
    }
    fetchSavedAddresses();
  }, [user, fetchSavedAddresses]);

  const appendSavedAddress = useCallback((address) => {
    setSavedAddresses(prev => [...prev, address]);
  }, []);

  const updateSavedAddress = useCallback((id, patch) => {
    setSavedAddresses(prev => prev.map(a => (String(a.id) === String(id) ? { ...a, ...patch } : a)));
  }, []);

  // Several addresses can share the same phone number, so this patches every
  // match in a single state update.
  const markAddressesPhoneVerified = useCallback((userMobileNumberId) => {
    setSavedAddresses(prev => prev.map(a => (
      String(a.user_mobile_number_id) === String(userMobileNumberId)
        ? { ...a, mobile_number_verified: true }
        : a
    )));
  }, []);

  const deleteSavedAddress = useCallback((id) => {
    setSavedAddresses(prev => prev.filter(a => String(a.id) !== String(id)));
  }, []);

  const pickAddress = useCallback(async (id) => {
    if (!user) {
      toast.error('Please log in to select an address');
      return { ok: false, error: 'Not authenticated' };
    }

    const idStr = String(id);
    const prevPicked = savedAddresses.find(a => a.is_picked);
    const prevPickedId = prevPicked ? String(prevPicked.id) : null;
    if (prevPickedId === idStr) return { ok: true };

    setSavedAddresses(prev => prev.map(a => {
      if (String(a.id) === idStr) return { ...a, is_picked: true };
      if (prevPickedId != null && String(a.id) === prevPickedId) return { ...a, is_picked: false };
      return a;
    }));

    try {
      const { data } = await getApiClient().setAddressAsPicked(id);
      if (!data.success) {
        throw new Error(data.message || 'Failed to select address');
      }
      cart.forceRefresh().catch(() => {});
      return { ok: true };
    } catch (err) {
      setSavedAddresses(prev => prev.map(a => {
        if (String(a.id) === idStr) return { ...a, is_picked: false };
        if (prevPickedId != null && String(a.id) === prevPickedId) return { ...a, is_picked: true };
        return a;
      }));
      toast.error(err.message || 'Failed to select address');
      return { ok: false };
    }
  }, [savedAddresses, user, cart]);

  const setDefaultAddress = useCallback(async (id) => {
    const idStr = String(id);
    const prevDefault = savedAddressesRef.current.find(a => a.is_default);
    const prevDefaultId = prevDefault ? String(prevDefault.id) : null;

    setSavedAddresses(prev => prev.map(a => ({ ...a, is_default: String(a.id) === idStr })));

    try {
      const { data } = await getApiClient().setDefaultAddress(id);
      if (!data.success) throw new Error(data.message || 'Failed to set default');
      return { ok: true };
    } catch (err) {
      // Rolls back to the snapshot taken before the optimistic update. Concurrent
      // mutations between the optimistic write and this rollback are accepted as
      // a known edge case.
      setSavedAddresses(prev => prev.map(a => ({
        ...a,
        is_default: prevDefaultId != null ? String(a.id) === prevDefaultId : false
      })));
      toast.error(err.message || 'Failed to set default address');
      return { ok: false };
    }
  }, []);

  const createSavedAddress = useCallback(
    async (
      addressCountry,
      label,
      isDefault,
      firstName,
      lastName,
      mobileNumber,
      mobileCountryCode,
      mobileAddressCountry,
      specialInstructions,
      stepsData,
      addressLine
    ) => {
      if (!user) {
        return { success: false, error: 'You must be logged in to save an address' };
      }

      const addressData = { ...stepsData };
      addressData.address_country = addressData.country_iso2 || addressCountry;
      delete addressData.country_iso2;

      addressData.address_line_0 =
        addressLine ||
        addressData.address_line_0 ||
        stepsData.building ||
        stepsData.street ||
        '';

      addressData.mobile_address_country =
        mobileAddressCountry ||
        addressData.mobile_address_country ||
        addressData.address_country;

      addressData.mobile_country_code =
        mobileCountryCode ||
        addressData.mobile_country_code ||
        '';

      addressData.label = label;
      addressData.is_default = isDefault;
      addressData.first_name = firstName;
      addressData.last_name = lastName;
      addressData.mobile_number = mobileNumber;
      addressData.special_instructions = specialInstructions;

      try {
        const prevIds = new Set(savedAddressesRef.current.map(a => String(a.id)));

        const { data } = await getApiClient().createSavedAddress(addressData);

        if (!data.success) {
          return { success: false, error: data.message || 'Failed to create address' };
        }

        const { data: refreshData } = await getApiClient().getAllMySavedAddresses();
        if (!refreshData.success || !Array.isArray(refreshData.user_saved_addresses)) {
          toast.error('Address saved but failed to refresh the list');
          return { success: true };
        }

        const sortedAddresses = [...refreshData.user_saved_addresses].sort((a, b) => a.id - b.id);
        setSavedAddresses(sortedAddresses);
        cart.forceRefresh().catch(() => {});
        const newAddress = sortedAddresses.find(a => !prevIds.has(String(a.id)));
        return { success: true, address: newAddress || null };
      } catch (error) {
        let resolvedErrorMessage;
        handleApiError(error, (message) => {
          resolvedErrorMessage = message;
        }, null, true);
        return { success: false, error: resolvedErrorMessage };
      }
    // fetchSavedAddresses is intentionally absent: after creating, we call
    // getAllMySavedAddresses() inline above rather than going through that helper.
    }, [user, cart]);

  return (
    <SavedAddressesContext.Provider value={{
      savedAddresses,
      loaded,
      fetchSavedAddresses,
      appendSavedAddress,
      updateSavedAddress,
      markAddressesPhoneVerified,
      deleteSavedAddress,
      setDefaultAddress,
      createSavedAddress,
      pickAddress
    }}>
      {children}
    </SavedAddressesContext.Provider>
  );
};

export const useSavedAddresses = () => {
  const ctx = useContext(SavedAddressesContext);
  if (!ctx) {
    throw new Error('useSavedAddresses must be used within a SavedAddressesProvider');
  }
  return ctx;
};
